<script setup lang="ts">
import { EIcon, ETooltip, useIsRtl, type IconName } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { tParts } from '@/i18n'
import { useAuthStore } from '@/stores/auth'

const props = defineProps<{ collapsed: boolean }>()

const route = useRoute()
const auth = useAuthStore()
const { t, locale } = useI18n()

interface NavItem {
  to: string
  label: string
  icon: IconName
  /** Hidden entirely when false — §11: an action you cannot take is not shown. */
  visible: boolean
  description: string
}

interface NavGroup {
  /** Stable, locale-independent — it becomes the heading's `id`. */
  key: string
  /** `null` for the opening run of rows, which needs no heading above it. */
  label: string | null
  items: NavItem[]
}

/**
 * Two groups, not eight flat rows.
 *
 * The first five rows are one thing: the tenants this deployment runs and the
 * commercial machinery around them. Geography and tax packs are not that —
 * they are the operator's own *content*, curated once for the platform and
 * inherited by every tenant (C0, docs/design/C0_TAX_SPINE.md). A tax pack is
 * not owned by a tenant the way a subscription is, so filing it beside
 * "Tenants" would teach the wrong model of what the operator is editing.
 *
 * A group rather than a single rail entry with a second level (the shape
 * Billing uses) because these three screens are peers reached from each other
 * as often as from the rail, and none of them is the obvious parent: the
 * geography master outlives every pack, and adoption is read while a pack is
 * open. Billing's three lists, by contrast, hang off one subject.
 */
const groups = computed<NavGroup[]>(() => [
  {
    key: 'operations',
    label: null,
    items: [
      {
        to: '/',
        label: t('nav.platform.label'),
        icon: 'info',
        visible: true,
        description: t('nav.platform.description'),
      },
      {
        to: '/tenants',
        label: t('nav.tenants.label'),
        icon: 'inbox',
        visible: true,
        description: t('nav.tenants.description'),
      },
      {
        to: '/plans',
        label: t('nav.plans.label'),
        icon: 'columns',
        visible: true,
        description: t('nav.plans.description'),
      },
      {
        // Billing takes one rail entry; its three lists are the second level
        // (modules/billing/BillingNav.vue).
        to: '/billing',
        label: t('nav.billing.label'),
        icon: 'download',
        visible: true,
        description: t('nav.billing.description'),
      },
      {
        to: '/admins',
        label: t('nav.admins.label'),
        icon: 'check',
        visible: true,
        description: t('nav.admins.description'),
      },
    ],
  },
  {
    key: 'localization',
    label: t('nav.group.localization'),
    items: [
      /*
       * All three are visible to every platform role, and that is the server's
       * answer rather than a guess: `internal/adminapi/routes.go` gates
       * `GET /geo/*` and `GET /tax-packs*` (PermGeoRead, PermTaxPackRead) with
       * `RequireRole(rolesAny...)` — owner, support AND billing — and the
       * adoption dashboard reads `GET /tenants` plus
       * `GET /tenants/:id/tax-packs`, both `rolesAny` too. Hiding these from
       * the billing role would be hiding a door that opens.
       *
       * The narrower gates are all on writes (`rolesOwnerSupport` to author a
       * pack, `rolesOwnerOnly` to publish or withdraw one), and those live on
       * the buttons inside the screens, where the screens already put them —
       * not on the way in.
       */
      {
        to: '/geography',
        label: t('nav.geography.label'),
        icon: 'external',
        visible: true,
        description: t('nav.geography.description'),
      },
      {
        to: '/tax-packs',
        label: t('nav.taxPacks.label'),
        icon: 'filter',
        visible: true,
        description: t('nav.taxPacks.description'),
      },
      {
        // `/tax-packs/:id` and `/tax-packs/tenants/:tenantId` are deliberately
        // absent: both need an id the rail cannot supply, and both are reached
        // from a list that is on it.
        to: '/tax-pack-adoption',
        label: t('nav.taxPackAdoption.label'),
        icon: 'refresh',
        visible: true,
        description: t('nav.taxPackAdoption.description'),
      },
    ],
  },
])

const visibleGroups = computed(() =>
  groups.value
    .map((group) => ({ ...group, items: group.items.filter((item) => item.visible) }))
    .filter((group) => group.items.length > 0),
)

/**
 * `startsWith` is why `/tax-packs` and `/tax-pack-adoption` are two separate
 * checks and not one: `/tax-pack-adoption`.startsWith('/tax-pack') is true, so
 * a prefix match on a shorter row would light up both. It does not here —
 * '/tax-pack-adoption'.startsWith('/tax-packs') is false — but the detail
 * route `/tax-packs/42` does have to light up `/tax-packs`, which is exactly
 * what the prefix gives.
 */
function isActive(to: string): boolean {
  return to === '/' ? route.path === '/' : route.path.startsWith(to)
}

const width = computed(() => (props.collapsed ? 'w-14' : 'w-56'))

/**
 * `ETooltip`'s `side` is physical, and the collapsed rail sits on the *end* of
 * the viewport — the right in English, the left in Dhivehi. A hard-coded
 * "right" would open every label off the edge of an RTL screen.
 */
const isRtl = useIsRtl()
const tooltipSide = computed(() => (isRtl.value ? 'left' : 'right'))

/** The operator's own role name, in their language rather than the API's. */
const roleName = computed(() => {
  const role = auth.role
  if (role === null) return ''
  const key = `common.roles.${role}`
  return t(key) === key ? role : t(key)
})

/**
 * "Signed in as {role}." with the role emphasised: the sentence is split at its
 * own placeholder so the emphasis lands wherever the language puts the word.
 */
const signedInParts = computed(() => {
  void locale.value
  return tParts('nav.signedInAs', 'role')
})
</script>

<template>
  <!--
    Sticky, viewport-tall column (the top bar is 3rem). A nav sized to its own
    content leaves a seam where the raised surface stops, and on a long screen
    the operator would have to scroll back up to change section.
  -->
  <nav
    :class="[
      'sticky top-12 h-[calc(100vh-3rem)] shrink-0 overflow-y-auto border-e border-subtle bg-raised transition-[width]',
      width,
    ]"
    :aria-label="$t('nav.primaryLabel')"
  >
    <template v-for="group in visibleGroups" :key="group.key">
      <!--
        A collapsed rail is 3.5rem wide and cannot hold a heading, so the
        heading goes to `sr-only` and a rule carries the grouping visually.
        It is never dropped outright: `aria-labelledby` below points at it, and
        a screen reader gets the same two sections either way.
      -->
      <template v-if="group.label !== null">
        <hr v-if="collapsed" class="mx-3 mt-2 border-t border-subtle" aria-hidden="true" />
        <p
          :id="`nav-group-${group.key}`"
          :class="
            collapsed
              ? 'sr-only'
              : 'mt-3 px-2 pb-1 text-2xs font-medium tracking-wide text-muted uppercase'
          "
        >
          {{ group.label }}
        </p>
      </template>

      <ul
        class="flex flex-col gap-px p-2"
        :class="group.label !== null ? 'pt-0' : ''"
        :aria-labelledby="group.label !== null ? `nav-group-${group.key}` : undefined"
      >
        <li v-for="item in group.items" :key="item.to">
          <ETooltip :content="item.label" :side="tooltipSide" :disabled="!collapsed">
            <RouterLink
              :to="item.to"
              :aria-current="isActive(item.to) ? 'page' : undefined"
              :title="collapsed ? undefined : item.description"
              :class="[
                'flex items-center gap-2 rounded px-2 py-1.5 text-sm outline-none',
                isActive(item.to)
                  ? 'bg-selected text-accent-text font-medium'
                  : 'text-secondary hover:bg-hover',
              ]"
            >
              <EIcon :name="item.icon" :size="16" />
              <span v-if="!collapsed" class="truncate">{{ item.label }}</span>
              <span v-else class="sr-only">{{ item.label }}</span>
            </RouterLink>
          </ETooltip>
        </li>
      </ul>
    </template>

    <p
      v-if="!collapsed && auth.role !== null"
      class="mt-2 border-t border-subtle px-4 py-3 text-2xs leading-snug text-muted"
    >
      {{ signedInParts[0] }}<span class="font-medium text-secondary">{{ roleName }}</span>{{ signedInParts[1] }}
      <template v-if="auth.role === 'billing'">
        {{ $t('nav.roleNote.billing') }}
      </template>
      <template v-else-if="auth.role === 'support'">
        {{ $t('nav.roleNote.support') }}
      </template>
    </p>
  </nav>
</template>
